/**
 * Simple event emitter implementation for handling events in services
 */
export class EventEmitter {
  constructor() {
    this.events = new Map();
  }

  /**
   * Subscribe to an event
   * @param {string} event - The event name to subscribe to
   * @param {Function} callback - The callback to execute when the event occurs
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event).add(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - The event name to unsubscribe from
   * @param {Function} callback - The callback to remove
   */
  off(event, callback) {
    if (!this.events.has(event)) return;
    const callbacks = this.events.get(event);
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      this.events.delete(event);
    }
  }

  /**
   * Emit an event with data
   * @param {string} event - The event name to emit
   * @param {any} data - The data to pass to subscribers
   */
  emit(event, data) {
    if (!this.events.has(event)) return;
    const callbacks = this.events.get(event);
    for (const callback of callbacks) {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    }
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners() {
    this.events.clear();
  }

  /**
   * Get all registered event names
   * @returns {string[]} Array of event names
   */
  eventNames() {
    return Array.from(this.events.keys());
  }

  /**
   * Get number of listeners for an event
   * @param {string} event - The event name
   * @returns {number} Number of listeners
   */
  listenerCount(event) {
    return this.events.has(event) ? this.events.get(event).size : 0;
  }
} 